import { AdvancedAnalysis } from "../models/AdvancedAnalysis";
import { AdvancedAnalysisField } from "../models/AdvancedAnalysisField";
import { AdvancedAnalysisFieldType } from "../models/AdvancedAnalysisFieldType";
import { AdvancedAnalysisVisibilityFieldTypeValues } from "../models/AdvancedAnalysisFieldTypeArgs";
import { AdvancedAnalysisSchema } from "../models/AdvancedAnalysisSchema";
import { AdvancedAnalysisSchemaUiFieldGroup } from "../models/AdvancedAnalysisSchemaUi";
import { AdvancedAnalysisService } from "./AdvancedAnalysisService";
import { AdvancedAnalysisTypeGuardService } from "./AdvancedAnalysisTypeGuardService";

export class AdvancedAnalysisUiService {

  public static getAdvancedAnalysisSchemaUiFieldGroups = (
    advancedAnalysisSchema: AdvancedAnalysisSchema
  ): Array<AdvancedAnalysisSchemaUiFieldGroup> | null => {

    if (!AdvancedAnalysisTypeGuardService.hasDefinedAdvancedAnalysisSchemaUi(advancedAnalysisSchema)) return null;
    return advancedAnalysisSchema.ui?.field_groups as Array<AdvancedAnalysisSchemaUiFieldGroup>;

  }

  public static getValueByPath = (configuration: any, path?: string | string[]): any => {
    if (configuration == null) return undefined;
    if (path === undefined || path === null) return undefined;

    const parts: string[] = Array.isArray(path)
      ? path
      : (String(path).match(/[^.[\]]+/g) || []);

    if (parts.length === 0) return configuration;



    let cur: any = configuration;
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (cur == null) return undefined;

      if (part === '*') {
        if (!Array.isArray(cur)) return undefined;
        const remainingParts = parts.slice(i + 1);
        return cur.map((item: any) => AdvancedAnalysisUiService.getValueByPath(item, remainingParts));
      }

      // treat purely-numeric segments as indices
      if (/^\d+$/.test(part)) {
        const idx = Number(part);
        cur = cur[idx];
      } else {
        cur = cur[part];
      }
    }
    return cur;
  }

  // Attempts to determine field visibility based on field visible configuration and optional IAdvancedAnalysisVisibilityFieldTypeArgs configuration
  // _currentPath is not supported full in ObjectAnalysisConfigBuilder as we have not implemented pathRefs/method for getting current relative path in that component
  public static isFieldVisibleFromRoot = (field: AdvancedAnalysisField, rootConfiguration?: AdvancedAnalysis | null, _currentPath?: string[]) => {

    if (field.visible === false) return false;
    if (rootConfiguration != null) {
      // Root advancedAnalysis object is AnalysisConfig type and must be flattened for paths to be resolved
      const flattenedRootConfiguration = AdvancedAnalysisService.getFlattenedAdvancedAnalysis(rootConfiguration, null);

      const visibleConfigs = field?.field_type_args?.visible_configs;
      if (Array.isArray(visibleConfigs)) {

        let visible = false;
        visibleConfigs.forEach((config: { visible_prop_path: string, visible_prop_value: any, visible_value?: AdvancedAnalysisVisibilityFieldTypeValues, visible_parent?: string }) => {
          const propPath = config?.visible_prop_path;
          const expected = config?.visible_prop_value;
          const expectedValueType = config?.visible_value;
          const visibleParent = config?.visible_parent;

          let targetPathParts: string[] = [];

          if (visibleParent && Array.isArray(_currentPath)) {
            let foundIndex = -1;
            // Find nearest occurrence of visibleParent followed by numeric index
            for (let i = _currentPath.length - 1; i >= 0; i--) {
              if (_currentPath[i] === visibleParent && i + 1 < _currentPath.length && /^\d+$/.test(String(_currentPath[i + 1]))) {
                foundIndex = i;
                break;
              }
            }
            if (foundIndex !== -1) {
              const base = _currentPath.slice(0, foundIndex + 2); // includes parent and index
              const propParts = propPath.includes('.') ? propPath.split('.') : [propPath];
              targetPathParts = [...base, ...propParts];
            }
          }

          if (targetPathParts.length === 0) {
            targetPathParts = propPath.includes('.')
              ? propPath.split('.')
              : (_currentPath ? [..._currentPath.slice(0, -1), propPath] : [propPath]);
          }

          const val = AdvancedAnalysisUiService.getValueByPath(flattenedRootConfiguration, targetPathParts);
          const flatVal = Array.isArray(val) ? val.flat(Infinity) : val;
          const matches = Array.isArray(flatVal)
            ? flatVal.some((v: any) => this.valueMatchesAnalysisConfigVisibilityFieldTypeValue(v, expectedValueType, expected) || v === expected)
            : this.valueMatchesAnalysisConfigVisibilityFieldTypeValue(flatVal, expectedValueType, expected) || flatVal === expected;
          visible = visible || matches;

        })
        return visible;
      }

    }
    return true;
  }

  private static valueMatchesAnalysisConfigVisibilityFieldTypeValue(value: any, visibilityValue?: AdvancedAnalysisVisibilityFieldTypeValues, expected?: any): boolean {

    switch (visibilityValue) {
      case AdvancedAnalysisVisibilityFieldTypeValues.Defined:
        return value !== undefined;
      case AdvancedAnalysisVisibilityFieldTypeValues.Falsey:
        return !value;
      case AdvancedAnalysisVisibilityFieldTypeValues.Truthy:
        return value;
      case AdvancedAnalysisVisibilityFieldTypeValues.Contains:
        if (Array.isArray(expected) || typeof expected === 'string') {
          return expected.includes(value);
        }
        console.warn(`AdvancedAnalysisUiService: visible_value 'contains' requires visible_prop_value to be an array or string, got: ${typeof expected}`);
        return false;
      case AdvancedAnalysisVisibilityFieldTypeValues.DoesNotContain:
        if (Array.isArray(expected) || typeof expected === 'string') {
          return !expected.includes(value);
        }
        console.warn(`AdvancedAnalysisUiService: visible_value 'does_not_contain' requires visible_prop_value to be an array or string, got: ${typeof expected}`);
        return false;
      default:
        return false
    }

  }

  public static isValueMappedField = (field: AdvancedAnalysisField): boolean => {
    const srcPropPath: string | undefined = field.field_type_args?.value_map?.src_prop_path
    return typeof srcPropPath === 'string' && srcPropPath.trim().length > 0
  }

  //@ts-ignore
  public static transformValueForValueMappedField = (field: AdvancedAnalysisField, newValue: any, oldValue?: any): any => {

    switch (field.field_type) {
      // Single value fields
      case AdvancedAnalysisFieldType.Text:
      case AdvancedAnalysisFieldType.Number:
      case AdvancedAnalysisFieldType.Boolean:
      case AdvancedAnalysisFieldType.Select:
      case AdvancedAnalysisFieldType.SelectOrCreate:
        return newValue;
      case AdvancedAnalysisFieldType.MultiSelect:
      case AdvancedAnalysisFieldType.ArrayValues:
        let value = newValue !== undefined ? [newValue] : [];
        if (Array.isArray(oldValue)) {
          value = [...value, ...oldValue.filter(v => v !== newValue)];
        }
        return value;

      default:
        console.warn("Unsupported AdvancedAnalysisFieldType encountered transforming value for ValueMapped field!");
    }

  }

}
